import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertCircle, ChevronRight, Clock, Calendar, DollarSign, CheckCircle, XCircle, AlertTriangle, Loader } from 'lucide-react';
import './style.css';
import axios from 'axios';
import WhatsAppFloat from './auth/WhatsAppFloat';

const CashoutView = ({ setCurrentView, userDetails, token, calculateTotalBalance, setToastNotification }) => {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasInvestments, setHasInvestments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresProof, setRequiresProof] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchWithdrawalHistory(),
      checkInvestments(),
      checkProofRequirement()
    ]).finally(() => setIsLoading(false));
  }, []);

  const checkProofRequirement = async () => {
    try {
      const response = await axios.get(
        `https://api.visiontech2030.com/api/withdrawals/proof-requirement/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setRequiresProof(response.data.requiresProof);
    } catch (error) {
      console.error('Error checking proof requirement:', error);
      setToastNotification({ 
        message: 'Failed to check proof requirement', 
        type: 'error' 
      });
    }
  };

  const checkInvestments = async () => {
    try {
      const response = await axios.get(
        `https://api.visiontech2030.com/api/investments/user/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setHasInvestments(response.data.length > 0);
    } catch (error) {
      console.error('Error checking investments:', error);
      setToastNotification({ 
        message: 'Failed to check investment status', 
        type: 'error' 
      });
    }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const response = await axios.get(
        `https://api.visiontech2030.com/api/withdrawals/history/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setWithdrawalHistory(response.data);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      setToastNotification({ 
        message: 'Failed to fetch withdrawal history', 
        type: 'error' 
      });
    }
  };

  const handleWithdrawal = async () => {
    if (requiresProof) {
      setErrorMessage('You must submit a proof for your previous withdrawal before making a new request.');
      return;
    }
    if (!hasInvestments) {
      setErrorMessage('You need to have at least one investment before making a withdrawal. Browse our investment products to get started!');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const response = await axios.post(
        'https://api.visiontech2030.com/api/withdrawals/request',
        {
          email: userDetails.email,
          amount: withdrawAmount
        },
        {
          headers: { 'Authorization': token }
        }
      );

      setToastNotification({ 
        message: 'Withdrawal request submitted successfully', 
        type: 'success' 
      });
      setWithdrawAmount('');
      fetchWithdrawalHistory();
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to process withdrawal request';
      setErrorMessage(errorMessage);
      setToastNotification({ message: errorMessage, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to get status icon
  const getStatusIcon = (status) => {
    switch(status.toLowerCase()) {
      case 'pending':
        return <Clock size={16} />;
      case 'approved':
        return <CheckCircle size={16} />;
      case 'completed':
        return <CheckCircle size={16} />;
      case 'rejected':
        return <XCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (isLoading) {
    return (
      <div className="vt-loading-container">
        <div className="vt-loading-spinner">
          <Loader size={30} className="vt-spinner-icon" />
        </div>
        <p className='paragraph'>Loading your wallet...</p>
      </div>
    );
  }

  return (
    <div className="vt-cashout-container">
      <div className="vt-cashout-header">
        <button className="vt-cashout-back-btn" onClick={() => setCurrentView('main')}>
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <h2 className="vt-cashout-title">My Wallet</h2>
      </div>

      <div className="vt-cashout-content">
        <div className="vt-balance-card">
          <div className="vt-balance-card-glow"></div>
          <div className="vt-balance-card-content">
            <div className="vt-balance-details">
              <span className="vt-balance-label">Total Available Balance</span>
              <h1 className="vt-balance-amount">KES {calculateTotalBalance()}</h1>
            </div>
            <div className="vt-balance-decoration">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="38" stroke="rgba(255,255,255,0.1)" strokeWidth="4"/>
                <path d="M40 2C18.9543 2 2 18.9543 2 40C2 61.0457 18.9543 78 40 78" stroke="rgba(255,255,255,0.5)" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="vt-cashout-form-section">
          <div className="vt-section-header">
            <h3 className="vt-section-title">Request Withdrawal</h3>
            <div className="vt-section-divider"></div>
          </div>
          
          {!hasInvestments && (
            <div className="vt-info-alert">
              <div className="vt-alert-icon">
                <AlertCircle size={20} />
              </div>
              <p className="vt-alert-message">
                You need to have at least one active investment to make withdrawals. 
                <button 
                  className="vt-alert-link"
                  onClick={() => setCurrentView('investments')}
                >
                  Browse investment opportunities
                </button>
              </p>
            </div>
          )}
          
          {errorMessage && (
            <div className="vt-error-alert">
              <div className="vt-alert-icon">
                <AlertTriangle size={20} />
              </div>
              <p className="vt-alert-message">{errorMessage}</p>
            </div>
          )}
          
          <div className="vt-cashout-input-group">
            <div className="vt-input-wrapper">
              <span className="vt-currency-prefix">KES</span>
              <input 
                type="number" 
                placeholder="Enter withdrawal amount" 
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="vt-amount-input"
                disabled={!hasInvestments || requiresProof}
              />
            </div>
            <p className="vt-input-hint">Minimum withdrawal amount: KES 100</p>
            <button 
              className="vt-withdraw-button"
              onClick={handleWithdrawal}
              disabled={isProcessing || !withdrawAmount || Number(withdrawAmount) < 100 || !hasInvestments || requiresProof}
            >
              {isProcessing ? (
                <>
                  <Loader size={16} className="vt-button-spinner" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <DollarSign size={16} />
                  <span>Withdraw Funds</span>
                </>
              )}
            </button>
          </div>
        </div>

        {requiresProof && (
          <div className="vt-proof-notice">
            <div className="vt-proof-notice-icon">
              <AlertCircle size={20} />
            </div>
            <div className="vt-proof-notice-content">
              <h4 className="vt-proof-notice-title">Proof Required</h4>
              <p className="vt-proof-notice-message">
                You must submit a proof for your previous withdrawal before making a new request.
              </p>
              <button 
                className="vt-proof-notice-button"
                onClick={() => setCurrentView('proofs')}
              >
                Submit Proof
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        <div className="vt-withdrawal-history-section">
          <div className="vt-section-header">
            <h3 className="vt-section-title">Withdrawal History</h3>
            <div className="vt-section-divider"></div>
          </div>
          
          {withdrawalHistory.length === 0 ? (
            <div className="vt-empty-history">
              <p className='paragraph'>You haven't made any withdrawal requests yet.</p>
            </div>
          ) : (
            <div className="vt-history-list">
              {withdrawalHistory.map((request, index) => (
                <div key={index} className="vt-history-card">
                  <div className="vt-history-card-header">
                    <div className={`vt-status-badge status-${request.withdrawalStatus.toLowerCase()}`}>
                      {getStatusIcon(request.withdrawalStatus)}
                      <span>{request.withdrawalStatus}</span>
                    </div>
                    <div className="vt-history-date">
                      <Calendar size={14} />
                      <span>{formatDate(request.requestTime)}</span>
                    </div>
                  </div>
                  
                  <div className="vt-history-card-body">
                    <div className="vt-history-amount-row">
                      <div className="vt-history-amount-item">
                        <span className="vt-history-label">Requested Amount</span>
                        <span className="vt-history-value">KES {request.requestedAmount.toLocaleString()}</span>
                      </div>
                      <div className="vt-history-amount-divider"></div>
                      <div className="vt-history-amount-item">
                        <span className="vt-history-label">To Receive</span>
                        <span className="vt-history-value highlight">KES {request.amountToReceive.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="vt-history-card-footer">
                    <div className="vt-history-ref">
                      <span className="vt-history-ref-label">Order:</span>
                      <span className="vt-history-ref-value">#{request.orderNumber}</span>
                    </div>
                    <div className="vt-history-time">
                      <Clock size={14} />
                      <span>{new Date(request.requestTime).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <WhatsAppFloat />
      
      <style jsx="true">{`
        .vt-cashout-container {
          padding: 16px;
          max-width: 800px;
          margin: 0 auto;
          color: #e2e8f0;
          margin-bottom: 3rem;
        }
        
        .vt-cashout-header {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-cashout-back-btn {
          background: rgba(79, 70, 229, 0.1);
          color: #a5b4fc;
          border: 1px solid rgba(79, 70, 229, 0.2);
          border-radius: 8px;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-cashout-back-btn:hover {
          background: rgba(79, 70, 229, 0.2);
          transform: translateX(-2px);
        }
        
        .vt-cashout-title {
          margin: 0 0 0 16px;
          font-size: 24px;
          font-weight: 700;
          color: #f8fafc;
          flex: 1;
        }
        
        .vt-cashout-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        
        /* Balance Card Styles */
        .vt-balance-card {
          background: linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%);
          border-radius: 16px;
          padding: 24px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(79, 70, 229, 0.2);
          margin-bottom: 8px;
        }
        
        .vt-balance-card-glow {
          position: absolute;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
          top: -75px;
          right: -50px;
        }
        
        .vt-balance-card-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .vt-balance-details {
          flex: 1;
        }
        
        .vt-balance-label {
          display: block;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 8px;
        }
        
        .vt-balance-amount {
          font-size: 32px;
          font-weight: 700;
          color: white;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .vt-balance-decoration {
          animation: rotateAnimation 8s linear infinite;
        }
        
        @keyframes rotateAnimation {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Form Section Styles */
        .vt-cashout-form-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .vt-section-header {
          margin-bottom: 16px;
          position: relative;
        }
        
        .vt-section-title {
          font-size: 18px;
          font-weight: 600;
          color: #f8fafc;
          margin: 0 0 8px;
        }
        
        .vt-section-divider {
          height: 2px;
          width: 40px;
          background: linear-gradient(to right, #4f46e5, transparent);
          border-radius: 1px;
        }
        
        .vt-info-alert, .vt-error-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          animation: fadeIn 0.3s ease;
        }
        
        .vt-info-alert {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
        }
        
        .vt-error-alert {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        
        .vt-alert-icon {
          color: #60a5fa;
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        .vt-error-alert .vt-alert-icon {
          color: #f87171;
        }
        
        .vt-alert-message {
          margin: 0;
          font-size: 14px;
          color: #cbd5e1;
          line-height: 1.4;
        }
        
        .vt-alert-link {
          display: inline-block;
          background: none;
          border: none;
          color: #818cf8;
          padding: 4px 0;
          margin-left: 4px;
          cursor: pointer;
          font-size: 14px;
          text-decoration: underline;
          transition: color 0.2s ease;
        }
        
        .vt-alert-link:hover {
          color: #a5b4fc;
        }
        
        .vt-cashout-input-group {
          margin-top: 20px;
        }
        
        .vt-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 0 16px;
          transition: all 0.2s ease;
        }
        
        .vt-input-wrapper:focus-within {
          border-color: #4f46e5;
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .vt-currency-prefix {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.6);
          margin-right: 8px;
        }
        
        .vt-amount-input {
          flex: 1;
          background: transparent;
          border: none;
          font-size: 16px;
          color: white;
          padding: 14px 0;
          outline: none;
          width: 100%;
        }
        
        .vt-amount-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
        
        .vt-amount-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .vt-input-hint {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin: 8px 0 16px;
        }
        
        .vt-withdraw-button {
          width: 100%;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 14px;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        
        .vt-withdraw-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-withdraw-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .vt-button-spinner {
          animation: spinner 1s linear infinite;
        }
        
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Proof Notice Styles */
        .vt-proof-notice {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1));
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          border: 1px solid rgba(245, 158, 11, 0.2);
          margin-bottom: 16px;
        }
        
        .vt-proof-notice-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(245, 158, 11, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f59e0b;
          flex-shrink: 0;
        }
        
        .vt-proof-notice-content {
          flex: 1;
        }
        
        .vt-proof-notice-title {
          font-size: 16px;
          font-weight: 600;
          color: #f8fafc;
          margin: 0 0 4px;
        }
        
        .vt-proof-notice-message {
          font-size: 14px;
          color: #cbd5e1;
          margin: 0 0 12px;
        }
        
        .vt-proof-notice-button {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(245, 158, 11, 0.2);
          border: 1px solid rgba(245, 158, 11, 0.3);
          color: #f59e0b;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-proof-notice-button:hover {
          background: rgba(245, 158, 11, 0.3);
          transform: translateX(2px);
        }
        
        /* Withdrawal History Styles */
        .vt-withdrawal-history-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .vt-empty-history {
          padding: 20px;
          text-align: center;
          color: #94a3b8;
          font-size: 15px;
          background: rgba(15, 23, 42, 0.3);
          border-radius: 8px;
          border: 1px dashed rgba(255, 255, 255, 0.1);
        }
        
        .vt-history-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .vt-history-card {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: transform 0.2s ease;
        }
        
        .vt-history-card:hover {
          transform: translateY(-2px);
        }
        
        .vt-history-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          background: rgba(15, 23, 42, 0.3);
        }
        
        .vt-status-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }
        
        .status-pending {
          background: rgba(250, 204, 21, 0.15);
          color: #facc15;
        }
        
        .status-approved {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }
        
        .status-completed {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }
        
        .status-rejected {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }
        
        .vt-history-date {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
        }
        
        .vt-history-card-body {
          padding: 16px;
        }
        
        .vt-history-amount-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .vt-history-amount-item {
          text-align: center;
          flex: 1;
        }
        
        .vt-history-amount-divider {
          width: 1px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
        }
        
        .vt-history-label {
          display: block;
          font-size: 12px;
          color: #94a3b8;
          margin-bottom: 4px;
        }
        
        .vt-history-value {
          font-size: 16px;
          font-weight: 600;
          color: #f8fafc;
        }
        
        .vt-history-value.highlight {
          color: #22c55e;
        }
        
        .vt-history-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          background: rgba(15, 23, 42, 0.3);
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .vt-history-ref {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .vt-history-ref-label {
          font-size: 12px;
          color: #94a3b8;
        }
        
        .vt-history-ref-value {
          font-size: 12px;
          font-weight: 500;
          color: #cbd5e1;
        }
        
        .vt-history-time {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #94a3b8;
        }
        
        /* Loading Container */
        .vt-loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 0;
          color: #a5b4fc;
        }
        
        .vt-loading-spinner {
          margin-bottom: 16px;
        }
        
        .vt-spinner-icon {
          animation: spin 1.5s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Responsive Styles */
        @media (max-width: 640px) {
          .vt-cashout-container {
            padding: 12px;
          }
          
          .vt-cashout-header {
            margin-bottom: 16px;
          }
          
          .vt-cashout-title {
            font-size: 20px;
          }
          
          .vt-balance-amount {
            font-size: 26px;
          }
          
          .vt-balance-card {
            padding: 20px;
          }
          
          .vt-cashout-form-section,
          .vt-withdrawal-history-section {
            padding: 16px;
          }
          
          .vt-history-amount-row {
            flex-direction: column;
            gap: 12px;
          }
          
          .vt-history-amount-divider {
            width: 80%;
            height: 1px;
            margin: 0 auto;
          }

          /* Continuing the style jsx from the previous section */

          .vt-history-amount-item {
            padding: 8px 0;
          }
          
          .vt-history-card-footer {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .vt-proof-notice {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .vt-proof-notice-icon {
            margin-bottom: 8px;
          }
        }
        
        @media (max-width: 480px) {
          .vt-cashout-back-btn span {
            display: none;
          }
          
          .vt-cashout-title {
            font-size: 18px;
          }
          
          .vt-section-title {
            font-size: 16px;
          }
          
          .vt-balance-card-content {
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 16px;
          }
          
          .vt-balance-decoration {
            display: none;
          }
          
          .vt-balance-amount {
            font-size: 24px;
          }
          
          .vt-withdraw-button {
            font-size: 14px;
            padding: 12px;
          }
          
          .vt-info-alert, .vt-error-alert {
            padding: 10px 12px;
          }
          
          .vt-alert-message {
            font-size: 13px;
          }
          
          .vt-alert-link {
            display: block;
            margin: 6px 0 0;
          }
          
          .vt-history-amount-item {
            padding: 6px 0;
          }
          
          .vt-history-value {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default CashoutView;